import {useContext} from "react";
import {MedicationAdministered} from "../../../../../../../../store/dutyManagerReport/actions/DutyManagerReportActionTypes";
import {DutyManagerReportEditableContext} from "../../../Context/DutyManagerReportEditable";
import React from "react";
import Select, {SingleValue} from "react-select";
import {useDispatch, useSelector} from "react-redux";
import {RootStore} from "../../../../../../../../store/Store";
import {
    convertEntitiesToDropdownProps,
    convertEntityToDropdownProp,
    DDProps
} from "../../../../../../../Dropdown/Helpers/dropdownUtils";
import {
    removeMedicationAdministered,
    setMedicationAdministered
} from "../../../../../../../../store/dutyManagerReport/actions/DutyManagerReportActions";
import {StaffLink} from "../../../../../../../../api/grs";
import {fetchDrugBatchesWithinPack} from "../../../../../../../../store/drugBatch/actions/DrugBatchActions";
import {getAssignedStaffFromCalendarEntry} from "../../../../../../AvailableShiftsList/Helpers/availableShiftsListHelpers";
import {DrugPack} from "../../../../../../../../api/mm";
import {TbTrash} from "react-icons/tb";

export interface MedicationAdministeredItemProps {
    administration: MedicationAdministered;
}

const MedicationAdministeredItem = (props: MedicationAdministeredItemProps) => {
    const editable = useContext(DutyManagerReportEditableContext);
    const dispatch = useDispatch();
    const administration = props.administration;
    const calendarEntryStore = useSelector((state: RootStore) => state.calendarEntry);
    const drugPacksStore = useSelector((state: RootStore) => state.drugPack);
    const drugBatchesStore = useSelector((state: RootStore) => state.drugBatches);
    const assignedStaff: StaffLink[] = calendarEntryStore.data
        ? getAssignedStaffFromCalendarEntry(calendarEntryStore.data).map((x) => x.user)
        : [];

    const deleteMedicationAdministered = () => {
        dispatch(removeMedicationAdministered(administration.uid));
    };

    const onPackChange = (option: SingleValue<DDProps>) => {
        const packId = +(option?.value ?? 0);

        dispatch(
            setMedicationAdministered({
                ...administration,
                packId: packId,
                batchId: 0
            })
        );

        if (packId > 0) dispatch(fetchDrugBatchesWithinPack(packId));
    };

    const onBatchChange = (option: SingleValue<DDProps>) => {
        dispatch(setMedicationAdministered({...administration, batchId: +(option?.value ?? 0)}));
    };

    const onStaffChange = (option: SingleValue<DDProps>) => {
        const user = assignedStaff.find((x) => x.staffId == option?.value);
        dispatch(
            setMedicationAdministered({
                ...administration,
                staffId: (option?.value as string) ?? "",
                staffName: user?.staffName ?? "Unknown Staff"
            })
        );
    };

    /** If staff members are removed the event this function will create a dropdown
    option for the missing user */
    const supplementAssignedStaff = (staff: StaffLink[]): StaffLink[] => {
        if (!administration.staffId || !administration.staffName) return staff;

        const staffList = [...staff];
        staffList.sort((a, b) => a.staffName.localeCompare(b.staffName));

        const isMissing = staffList.findIndex((x) => x.staffId == administration.staffId) < 0;
        if (isMissing)
            staffList.push({staffId: administration.staffId, staffName: administration.staffName});

        return staffList;
    };

    return (
        <>
            <div className="eos-drug-admin-pack">
                <Select
                    maxMenuHeight={200}
                    className="search-bar"
                    classNamePrefix="select-options"
                    options={convertEntitiesToDropdownProps<DrugPack>(
                        drugPacksStore.data ?? [],
                        (e) => e.name,
                        (e) => e.id
                    )}
                    onChange={onPackChange}
                    value={convertEntityToDropdownProp<DrugPack>(
                        drugPacksStore.data ?? [],
                        administration.packId,
                        (e) => e.name,
                        (e) => e.id
                    )}
                    noOptionsMessage={() => "No Drug Packs Found"}
                    placeholder="Select Pack"
                    isDisabled={!editable}
                    isClearable={false}
                    isSearchable={editable}
                    menuPortalTarget={document.body}
                    key={administration.uid + administration.packId}
                />
            </div>
            <div className="eos-drug-admin-drug">
                <Select
                    maxMenuHeight={200}
                    className="search-bar"
                    classNamePrefix="select-options"
                    options={convertEntitiesToDropdownProps(
                        drugBatchesStore.data?.get(administration.packId) ?? [],
                        (e) => `${e.name} (${e.batchNumber})`,
                        (e) => e.id
                    )}
                    onChange={onBatchChange}
                    value={convertEntityToDropdownProp(
                        drugBatchesStore.data?.get(administration.packId) ?? [],
                        administration.batchId,
                        (e) => `${e.name} (${e.batchNumber})`,
                        (e) => e.id
                    )}
                    noOptionsMessage={() => "No Drug Batches Found"}
                    placeholder="Select Batch"
                    isDisabled={!editable}
                    isClearable={false}
                    isSearchable={editable}
                    menuPortalTarget={document.body}
                    key={administration.uid + administration.batchId}
                />
            </div>
            <div className="eos-drug-admin-author">
                <Select
                    maxMenuHeight={200}
                    className="search-bar"
                    classNamePrefix="select-options"
                    options={convertEntitiesToDropdownProps(
                        supplementAssignedStaff(assignedStaff),
                        (e) => e.staffName,
                        (e) => e.staffId
                    )}
                    onChange={onStaffChange}
                    value={
                        administration.staffId
                            ? ({
                                  label: administration.staffName,
                                  value: administration.staffId
                              } as DDProps)
                            : undefined
                    }
                    noOptionsMessage={() => "No Clinicians Found"}
                    placeholder="Select Clinician"
                    isDisabled={!editable}
                    isClearable={false}
                    isSearchable={editable}
                    menuPortalTarget={document.body}
                    key={administration.uid + administration.staffId}
                />
            </div>
            <div className="eos-drug-admin-controls">
                <button
                    className="eos-icon-btn eos-icon-btn-danger"
                    onClick={deleteMedicationAdministered}
                    disabled={!editable}
                    title="Remove Administration"
                >
                    <TbTrash size={24} />
                </button>
            </div>
        </>
    );
};

export default MedicationAdministeredItem;
